'use client';
import React, { useState, useRef, useEffect } from 'react';
import { Bell, Sun, Moon, Check, Settings, LogOut, ChevronDown, User } from 'lucide-react';
import { useGlobalStore } from '@/store/useGlobalStore';
import AppLogo from '@/components/ui/AppLogo';
import NotificationPanel from '@/components/NotificationPanel';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/navigation';
import { fetchDesignAndWS, getDesignTitle } from '@/utils/api';
import appConfig from '@/config/appConfig';

const LANGUAGES = [
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { value: 'es', label: 'Español', flag: '🇪🇸' },
  { value: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { value: 'zh', label: '中文', flag: '🇨🇳' },
  { value: 'ar', label: 'العربية', flag: '🇸🇦' },
  { value: 'he', label: 'עברית', flag: '🇮🇱' },
];

export default function TopHeader() {
  const {
    user,
    activeAccount,
    unreadCount,
    theme,
    setTheme,
    language,
    setLanguage,
    logout,
    addToast,
  } = useGlobalStore();
  const { t } = useTranslation();
  const router = useRouter();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Design data from /design endpoint
  const [designLogo, setDesignLogo] = useState<string | null>(null);
  const [designTitle, setDesignTitle] = useState<string | null>(null);

  const applyDesign = (design: Record<string, unknown> | null, currentTheme: string) => {
    if (!design) return;
    const logoSrc =
      currentTheme === 'dark'
        ? (design?.logo_dark as string) || (design?.logo_light as string) || null
        : (design?.logo_light as string) || (design?.logo_dark as string) || null;
    setDesignLogo(logoSrc || null);
    const title = (design?.title as string) || (design?.company_name as string) || null;
    setDesignTitle(title);
  };

  useEffect(() => {
    // Always fetch fresh from /design endpoint — no localStorage fallback for logo
    fetchDesignAndWS()
      .then(() => {
        try {
          const raw = localStorage.getItem('design');
          if (raw) {
            const design = JSON.parse(raw);
            applyDesign(design, theme);
          }
        } catch {
          // ignore
        }
      })
      .catch(() => {});

    // Listen for design updates (e.g. triggered by fetchDesignAndWS)
    const handleDesignUpdated = () => {
      try {
        const raw = localStorage.getItem('design');
        if (raw) {
          const design = JSON.parse(raw);
          applyDesign(design, theme);
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener('designUpdated', handleDesignUpdated);
    return () => window.removeEventListener('designUpdated', handleDesignUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-resolve logo when theme changes
  useEffect(() => {
    try {
      const raw = localStorage.getItem('design');
      if (raw) {
        const design = JSON.parse(raw);
        applyDesign(design, theme);
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setShowLangPicker(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleThemeToggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
  };

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    setShowLangPicker(false);
  };

  const handleLogout = () => {
    setShowLogoutConfirm(false);
    logout();
    router.push('/login');
  };

  const currentLang = LANGUAGES.find((l) => l.value === language);

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-40 safe-top"
        style={{
          background: theme === 'dark' ? 'rgba(10,10,15,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div className="flex items-center justify-between px-4 h-14">
          {/* Left: Logo + account info */}
          <div className="flex items-center gap-2">
            {designLogo ? (
              <AppLogo size={28} src={designLogo} />
            ) : (
              <div
                className="w-7 h-7 rounded-lg flex-shrink-0"
                style={{ background: 'var(--muted)' }}
              />
            )}
            <div>
              <span className="font-bold text-sm text-foreground tracking-tight">
                {designTitle || getDesignTitle()}
              </span>
              {activeAccount && (
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-positive" />
                  <span className="text-[10px] text-muted-foreground font-mono">
                    #{activeAccount?.login}
                  </span>
                  <span
                    className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${
                      activeAccount?.type === 'live'
                        ? 'bg-positive-tint text-positive'
                        : 'bg-info-tint text-info'
                    }`}
                  >
                    {activeAccount?.type?.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-2">
            {/* Language picker */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setShowLangPicker(!showLangPicker)}
                className="p-2 rounded-xl transition-all active:scale-95 flex items-center gap-1"
                style={{ background: 'var(--muted)' }}
                aria-label={t('language')}
              >
                <span className="text-sm leading-none">{currentLang?.flag || '🌐'}</span>
                <span className="text-[10px] font-bold text-foreground">
                  {(language || 'en').toUpperCase()}
                </span>
              </button>

              <AnimatePresence>
                {showLangPicker && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full right-0 mt-2 rounded-2xl overflow-hidden shadow-2xl min-w-[160px]"
                    style={{
                      background: 'var(--card)',
                      border: '1px solid var(--border)',
                      zIndex: 9999,
                      boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                    }}
                  >
                    {LANGUAGES.map((lang) => (
                      <button
                        key={`lang-${lang.value}`}
                        onClick={() => handleLanguageChange(lang.value)}
                        className="w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors"
                        style={{
                          background:
                            language === lang.value ? 'rgba(245,158,11,0.08)' : 'transparent',
                          borderBottom: '1px solid var(--border)',
                        }}
                      >
                        <span className="text-base">{lang.flag}</span>
                        <span
                          className="text-sm font-medium flex-1"
                          style={{
                            color: language === lang.value ? 'var(--primary)' : 'var(--foreground)',
                          }}
                        >
                          {lang.label}
                        </span>
                        {language === lang.value && (
                          <Check size={13} style={{ color: 'var(--primary)' }} />
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Dark/Light mode toggle */}
            <button
              onClick={handleThemeToggle}
              className="p-2 rounded-xl transition-all active:scale-95"
              style={{ background: 'var(--muted)' }}
              aria-label={t('theme')}
            >
              {theme === 'dark' ? (
                <Sun size={17} className="text-foreground" />
              ) : (
                <Moon size={17} className="text-foreground" />
              )}
            </button>

            {/* Notifications */}
            {appConfig.headerNotifications && (
              <button
                onClick={() => setShowNotifications(true)}
                className="relative p-2 rounded-xl transition-all active:scale-95"
                style={{ background: 'var(--muted)' }}
                aria-label={t('notifications')}
              >
                <Bell size={18} className="text-foreground" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-negative text-white text-[9px] font-bold flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>
            )}

            {/* User menu dropdown (Settings + Logout) */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="p-2 rounded-xl transition-all active:scale-95 flex items-center gap-1"
                style={{ background: 'var(--muted)' }}
                aria-label="User menu"
              >
                <div
                  className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold"
                  style={{
                    background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
                    color: 'var(--primary-foreground)',
                  }}
                >
                  {user?.name?.charAt(0) || <User size={12} />}
                </div>
                <ChevronDown size={12} className="text-muted-foreground" />
              </button>

              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full right-0 mt-2 rounded-2xl overflow-hidden shadow-2xl min-w-[180px]"
                    style={{
                      background: 'var(--card)',
                      border: '1px solid var(--border)',
                      zIndex: 9999,
                      boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                    }}
                  >
                    {/* User info */}
                    <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
                      <p className="text-sm font-bold text-foreground truncate">
                        {user?.name || 'Trader'}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate">{user?.email}</p>
                    </div>

                    {/* Settings link */}
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        router.push('/settings');
                      }}
                      className="w-full px-4 py-3 text-left flex items-center gap-3 transition-colors"
                      style={{
                        background: 'transparent',
                        borderBottom: '1px solid var(--border)',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = 'rgba(245,158,11,0.06)')
                      }
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <Settings size={15} style={{ color: 'var(--muted-foreground)' }} />
                      <span className="text-sm font-medium text-foreground">
                        {t('headerSettings')}
                      </span>
                    </button>

                    {/* Logout link */}
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        setShowLogoutConfirm(true);
                      }}
                      className="w-full px-4 py-3 text-left flex items-center gap-3 transition-colors"
                      style={{ background: 'transparent' }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = 'rgba(239,68,68,0.06)')
                      }
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <LogOut size={15} style={{ color: 'var(--negative)' }} />
                      <span className="text-sm font-medium" style={{ color: 'var(--negative)' }}>
                        {t('headerLogout')}
                      </span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {showNotifications && <NotificationPanel onClose={() => setShowNotifications(false)} />}
      </AnimatePresence>

      {/* Logout Confirm Modal */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <ConfirmModal
            title={t('logOutConfirmTitle')}
            message={t('logOutConfirmMsg')}
            confirmLabel={t('logOut')}
            cancelLabel={t('stay')}
            destructive
            onConfirm={handleLogout}
            onCancel={() => setShowLogoutConfirm(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
